import React, { useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { TextField, Typography, Box } from "@mui/material";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  ORGANIZATION_TIER_TYPES,
  sale_categories,
} from "@/modules/common/constants";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { SingleAutoCompleteSelectChip } from "@/modules/common/components/input/single-autocomplete-select";
import MoneyInput from "@/modules/common/components/input/money-input";
import useReservedForm from "@/modules/lead-page/hooks/lead-info/status/forms/reserved-form.hook";
import FormFooter from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/form-footer";
import { StaticFormProps } from "@/modules/lead-page/types";
import { FileUploadButton } from "@/modules/common/components/input/file-upload/file-upload";
import SingleSelectSearch from "@/modules/common/components/input/single-autocomplete-search";
import {
  EnvironmentVariables,
  LocationDataParams,
  Options,
} from "@/modules/common/types";
import useLocationsData from "@/modules/common/hooks/locations-data.hook";
import { LocationsResponse } from "@/modules/common/dtos/index.v2";
import ReadOnlyTextInput from "@/modules/common/components/input/readonly-text-input";
import Grid from "@mui/material/GridLegacy";
import { SaleDocumentType } from "@/modules/leads/constants";
import { handleSaleCategoryChange } from "@/modules/lead-page/utils";
import { getReferralLabel } from "@/modules/common/utils";
import { useManageReferrals } from "@/modules/lead-page/hooks/manage-referrals.hook";

const ReserveForm: React.FC<StaticFormProps> = ({
  handleSaleSubmission,
  saleType,
  handleClose,
  sale,
  handleFiles,
  handlePaymentFiles,
  handlePaymentPlanFiles,
  handleDeletePaymentFiles,
  handleDeleteFile,
  isLoadingSubmition,
}) => {
  const [selectedUnitLocation, setSelectedUnitLocation] = useState<Options>();
  const showPaymentDocuments =
    `${EnvironmentVariables.COMPANY_NAME}` ===
      ORGANIZATION_TIER_TYPES.taskeen ||
    `${EnvironmentVariables.COMPANY_NAME}` === ORGANIZATION_TIER_TYPES.nawy;

  const {
    errors,
    control,
    onSubmit,
    handleSubmit,
    selectedSaleCategory,
    selectedCompound,
    saleDocumentTypeOption,
    setSaleDocumentTypeOption,
  } = useReservedForm({
    selectedUnitLocation,
    handleSaleSubmission,
    saleType,
    existingSale: sale,
  });

  const { referralsData } = useManageReferrals();
  const leadHasActiveReferral = referralsData?.data?.find(
    (referral) => referral.isExpired === false
  );

  const {
    locationsData: compoundsData,
    locationsLoading: compoundsLoading,
    locationSearchTerm: compoundsSearchTerm,
    handleSearchChange: handleCompoundSearchChange,
  } = useLocationsData({
    locationDataParams: LocationDataParams.COMPOUND,
  });

  const compoundOptions = useMemo(() => {
    const mapCompoundToOption = (
      location: LocationsResponse["data"]["results"][number]
    ) => ({
      value: location?.compound?.uid,
      optionValueUid: location?.compound?.area?.uid,
      label: `${location?.compound?.name} - ${location?.compound?.area?.name}`,
      developerName: location?.compound?.developer?.name ?? "",
    });

    const baseOptions = (compoundsData?.results || []).map(mapCompoundToOption);

    const saleCompoundOption = sale?.compound
      ? {
          value: sale.compound.uid,
          optionValueUid: sale.area?.uid,
          label: `${sale.compound.name} - ${sale.area?.name}`,
          developerName: sale.compound.developer?.name ?? "",
        }
      : null;

    return saleCompoundOption &&
      !baseOptions.find((opt) => opt.value === saleCompoundOption.value)
      ? [...baseOptions, saleCompoundOption]
      : baseOptions;
  }, [compoundsData?.results, sale?.compound, sale?.area]);

  const today = new Date();
  const minDate = new Date(today);
  minDate.setDate(today.getDate() - 1);
  const maxDate = new Date(today);
  maxDate.setDate(today.getDate() + 2);
  const maxContractDate = new Date(today);
  maxContractDate.setFullYear(today.getFullYear() + 1);
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h3_med">
          {sale ? "Edit" : "New"} {saleType} Form
        </Typography>
        <Typography variant="h4_med">
          {getReferralLabel({ sale, leadHasActiveReferral })}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item mobile={12} tablet={6}>
          <Controller
            name="unitValue"
            control={control}
            render={({ field }) => (
              <MoneyInput
                label="Unit Value"
                value={field.value || sale?.unitValue}
                onChange={(e) => field.onChange(e.target.value)}
                isError={!!errors.unitValue}
                helperText={errors.unitValue?.message}
              />
            )}
          />
        </Grid>
        <Grid item mobile={12} tablet={6}>
          <Controller
            name="unitNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Unit Number"
                InputLabelProps={{
                  shrink: field.value || sale?.unitNumber ? true : false,
                }}
                value={field.value || sale?.unitNumber}
                fullWidth
                error={!!errors.unitNumber}
                helperText={errors.unitNumber?.message}
              />
            )}
          />
        </Grid>

        <Grid item mobile={12} tablet={6}>
          <SingleSelectSearch
            value={selectedCompound || sale?.compound?.uid}
            defaultValue={selectedCompound || sale?.compound?.uid}
            size="medium"
            options={compoundOptions}
            isLoading={compoundsLoading}
            searchValue={compoundsSearchTerm}
            onSearchValueChange={handleCompoundSearchChange}
            name="compoundUId"
            control={control}
            label="Compound"
            error={errors.compoundUId?.message}
            onValueChange={(value) => {
              const selectedOption = compoundOptions.find(
                (opt) => opt.value === value
              );
              setSelectedUnitLocation(selectedOption);
            }}
          />
        </Grid>

        <Grid item mobile={12} tablet={6}>
          <ReadOnlyTextInput
            label="Developer"
            value={
              selectedUnitLocation?.developerName ||
              sale?.compound?.developer?.name ||
              ""
            }
          />
        </Grid>

        <Grid item mobile={12} tablet={6}>
          <Controller
            name="dateOfReservation"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date of Reservation"
                  value={field.value}
                  onChange={(newValue: Date | null) => {
                    const adjustedDate = newValue
                      ? new Date(newValue.setHours(12, 0, 0, 0))
                      : null;
                    field.onChange(adjustedDate);
                  }}
                  minDate={minDate}
                  maxDate={maxDate}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.dateOfReservation,
                      helperText: errors.dateOfReservation?.message,
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item mobile={12} tablet={6}>
          <Controller
            name="expectedContractionDate"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Expected Date of Contraction"
                  value={field.value}
                  onChange={(newValue: Date | null) => {
                    const adjustedDate = newValue
                      ? new Date(newValue.setHours(12, 0, 0, 0))
                      : null;
                    field.onChange(adjustedDate);
                  }}
                  maxDate={maxContractDate}
                  minDate={today}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.expectedContractionDate,
                      helperText: errors.expectedContractionDate?.message,
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item mobile={12} tablet={6}>
          <SingleAutoCompleteSelectChip
            name="saleCategory"
            control={control}
            value={selectedSaleCategory}
            options={sale_categories}
            onChange={(value) => {
              handleSaleCategoryChange(value, setSaleDocumentTypeOption);
            }}
            label="Sale Category"
            error={errors.saleCategory?.message}
          />
        </Grid>
        {selectedSaleCategory === "Nawy Now" && (
          <Grid item mobile={12} tablet={6}>
            <Controller
              name="cashPrice"
              control={control}
              render={({ field }) => (
                <MoneyInput
                  label="Cash Price"
                  onChange={(e) => field.onChange(e.target.value)}
                  defaultValue={field.value ? Number(field.value) : undefined}
                  isError={!!errors.cashPrice}
                  helperText={errors.cashPrice?.message}
                />
              )}
            />
          </Grid>
        )}
        <Grid item mobile={12}>
          <Controller
            name="comment"
            control={control}
            rules={{
              minLength: {
                value: 4,
                message: "Comment must be at least 4 characters long",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                margin="normal"
                label="Comment"
                InputLabelProps={{
                  shrink: field.value ? true : false,
                }}
                multiline
                rows={4}
                error={!!errors.comment}
                helperText={errors.comment?.message}
              />
            )}
          />
        </Grid>

        {showPaymentDocuments && (
          <>
            {saleDocumentTypeOption === SaleDocumentType.PAYMENT ||
              (saleDocumentTypeOption === SaleDocumentType.PAYMENT_PLAN && (
                <Grid
                  item
                  mobile={12}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Typography variant="h4_med">
                    {saleDocumentTypeOption === SaleDocumentType.PAYMENT_PLAN
                      ? "Upload Payment Plan Documents *"
                      : "Upload Payment Documents *"}
                  </Typography>
                  <FileUploadButton
                    setFiles={
                      saleDocumentTypeOption === SaleDocumentType.PAYMENT_PLAN
                        ? handlePaymentPlanFiles
                        : handlePaymentFiles
                    }
                    acceptedFileTypes={[
                      "image/jpeg",
                      "image/png",
                      "application/pdf",
                    ]}
                    onDeleteFile={handleDeletePaymentFiles}
                  />
                </Grid>
              ))}
          </>
        )}

        <Grid
          item
          mobile={12}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography variant="h4_med">Upload Documents</Typography>
          <FileUploadButton
            setFiles={handleFiles}
            acceptedFileTypes={["image/jpeg", "image/png", "application/pdf"]}
            onDeleteFile={handleDeleteFile}
          />
        </Grid>
        <FormFooter
          handleClose={handleClose}
          isLoadingSubmition={isLoadingSubmition}
          confirmText={sale ? "Update" : "Save"}
        />
      </Grid>
    </form>
  );
};

export default ReserveForm;
